/**
 * Portal Manta · Entry point Vite
 *
 * Antes de renderizar o React, tenta carregar os DADOS dos lotes do
 * SharePoint via Netlify Function. Se falhar, segue com os DADOS embutidos.
 * Badge fixo no canto inferior direito mostra a fonte (clique = ocultar).
 * Botão ↻ no badge força reload bypassando todos os caches.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import './styles/global.css';
import { carregarLotesDoSharePoint } from './data/sharepoint-loader.js';
import { mesclarDadosSharePoint } from './data/lotes-pr.js';

if (typeof window !== 'undefined') {
  window.MANTA_DATA_SOURCE = { type: 'loading', fetchedAt: null, lotes: [] };
}

function renderApp() {
  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
  installSourceBadge();
}

function renderLoading(msg) {
  const root = document.getElementById('root');
  if (!root) return;
  root.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui,sans-serif;color:#5D3A1A">
      <div style="text-align:center">
        <div style="width:32px;height:32px;border:3px solid #F5DCC8;border-top-color:#D4682A;border-radius:50%;animation:spin 0.8s linear infinite;margin:0 auto 12px"></div>
        <div style="font-size:14px;font-weight:600;margin-bottom:6px">Portal Manta</div>
        <div style="font-size:11px;color:#8B4A2D">${msg}</div>
        <style>@keyframes spin { to { transform: rotate(360deg) } }</style>
      </div>
    </div>`;
}

async function loadAndMerge({ refresh = false } = {}) {
  try {
    const remote = await carregarLotesDoSharePoint({ refresh });
    if (remote && remote.lotes && Object.keys(remote.lotes).length > 0) {
      const n = mesclarDadosSharePoint(remote.lotes);
      const keys = Object.keys(remote.lotes);
      console.info('✅ [Manta] %c%d lotes carregados do SharePoint%c (%s)%s',
        'color:#10B981;font-weight:bold', n, 'color:inherit',
        keys.join(', '),
        refresh ? ' [refresh forçado]' : '');
      console.info('   Atualizado em:', remote.meta?.fetchedAt);
      if (remote.meta?.errors?.length) {
        console.warn('   Alguns lotes deram erro:', remote.meta.errors);
      }
      window.MANTA_DATA_SOURCE = {
        type: 'sharepoint',
        fetchedAt: remote.meta?.fetchedAt,
        lotes: keys,
      };
      return true;
    }
    console.info('📦 [Manta] %cUsando DADOS embutidos (fallback)%c',
      'color:#F4A76A;font-weight:bold', 'color:inherit');
    window.MANTA_DATA_SOURCE = {
      type: 'fallback',
      reason: 'SharePoint não respondeu ou retornou vazio',
      lotes: [],
    };
    return false;
  } catch (err) {
    console.error('⚠️ [Manta] erro no loader, usando DADOS embutidos:', err);
    window.MANTA_DATA_SOURCE = { type:'error', reason:String(err.message || err), lotes:[] };
    return false;
  }
}

// Função pública: recarrega do SharePoint sem F5, atualiza badge, força React refresh.
async function recarregarDoSharePoint() {
  const badge = document.getElementById('manta-source-badge');
  if (badge) {
    badge.style.pointerEvents = 'none';
    badge.style.opacity = '0.6';
    const icon = badge.querySelector('.manta-icon');
    if (icon) icon.textContent = '⋯';
  }
  const ok = await loadAndMerge({ refresh: true });
  // Re-renderiza a árvore React inteira (já que os DADOS foram mutados, alguns
  // memoizados podem não refletir — reload é mais simples e barato).
  if (ok) {
    location.reload();
  } else {
    if (badge) {
      badge.style.pointerEvents = 'auto';
      badge.style.opacity = '1';
    }
    alert('Falha ao recarregar do SharePoint. Veja o console (F12).');
  }
}
if (typeof window !== 'undefined') {
  window.recarregarDoSharePoint = recarregarDoSharePoint;
}

function installSourceBadge() {
  if (typeof document === 'undefined') return;
  const src = (typeof window !== 'undefined' && window.MANTA_DATA_SOURCE) || { type:'unknown' };

  const palette = {
    sharepoint: { bg:'#10B981', fg:'#fff', icon:'☁', label:'SharePoint' },
    fallback:   { bg:'#F4A76A', fg:'#1A2840', icon:'📦', label:'Embutidos' },
    error:      { bg:'#EF4444', fg:'#fff', icon:'⚠', label:'Erro' },
    loading:    { bg:'#9CA3AF', fg:'#fff', icon:'⋯', label:'Carregando' },
    unknown:    { bg:'#9CA3AF', fg:'#fff', icon:'?',  label:'Desconhecido' },
  };
  const p = palette[src.type] || palette.unknown;

  const detalhes = src.type === 'sharepoint'
    ? `Lotes: ${(src.lotes||[]).join(', ') || '—'}\nAtualizado: ${src.fetchedAt || '—'}\n\nBotão ↻ força recarga do SharePoint (bypass cache).`
    : src.type === 'fallback'
      ? `Motivo: ${src.reason || 'desconhecido'}\n\nVerifique env vars no Netlify e permissões Graph.`
      : src.type === 'error' ? `Erro: ${src.reason || ''}` : '';

  const wrap = document.createElement('div');
  wrap.id = 'manta-source-badge';
  wrap.style.cssText = `
    position:fixed; bottom:10px; right:10px; z-index:99999;
    display:flex; align-items:stretch; gap:0;
    border-radius:99px; overflow:hidden;
    box-shadow:0 4px 12px rgba(0,0,0,.18);
    font:600 10.5px/1.2 system-ui,sans-serif; letter-spacing:.3px;
    transition:opacity .2s;
  `;

  const label = document.createElement('div');
  label.title = detalhes;
  label.style.cssText = `
    padding:6px 12px; background:${p.bg}; color:${p.fg};
    cursor:default; user-select:none;
    display:flex; align-items:center; gap:6px;
  `;
  label.innerHTML = `<span class="manta-icon" style="font-size:12px">${p.icon}</span> Dados: ${p.label}`;
  wrap.appendChild(label);

  // Só mostra o botão de refresh se está em modo SharePoint
  if (src.type === 'sharepoint') {
    const btn = document.createElement('button');
    btn.title = 'Recarregar do SharePoint (bypass cache)';
    btn.textContent = '↻';
    btn.style.cssText = `
      background:rgba(255,255,255,.22); color:#fff; border:0;
      padding:6px 11px; font:700 14px/1 system-ui,sans-serif;
      cursor:pointer; user-select:none;
      transition:background .15s;
    `;
    btn.onmouseenter = () => btn.style.background = 'rgba(255,255,255,.35)';
    btn.onmouseleave = () => btn.style.background = 'rgba(255,255,255,.22)';
    btn.onclick = (e) => { e.stopPropagation(); recarregarDoSharePoint(); };
    wrap.appendChild(btn);
  }

  // Hide button (× pequeno)
  const close = document.createElement('button');
  close.title = 'Ocultar badge';
  close.textContent = '×';
  close.style.cssText = `
    background:rgba(0,0,0,.15); color:${p.fg}; border:0;
    padding:6px 9px; font:700 13px/1 system-ui,sans-serif;
    cursor:pointer; user-select:none;
  `;
  close.onclick = () => { wrap.style.opacity = '0'; setTimeout(() => wrap.remove(), 200); };
  wrap.appendChild(close);

  document.body.appendChild(wrap);
}

(async () => {
  renderLoading('Carregando dados do SharePoint…');
  await loadAndMerge();
  renderApp();
})();
